import { TRACK_NAMES, TRACK_POINTS, type Category, type DisplayView, type Track } from '@banger/shared';
import { colorName } from './answerInput';

type Goal = Pick<DisplayView, 'mode' | 'track' | 'pattern'>;

/** The Album track in play, or null in Single. */
function albumTrack(view: Pick<DisplayView, 'mode' | 'track'>): Track | null {
  return view.mode === 'album' ? view.track : null;
}

/** Spin/Listening header: "Song 7" (Single) or "Track 2 · The Remix · Song 7" (Album). */
export function trackHeading(view: Pick<DisplayView, 'mode' | 'track' | 'roundNumber'>): string {
  const song = `Song ${view.roundNumber}`;
  return view.mode === 'album' && view.track ? `Track ${view.track} · ${TRACK_NAMES[view.track]} · ${song}` : song;
}

/** Standings headline for the current goal. */
export function standingsTitle(view: Pick<DisplayView, 'mode' | 'track' | 'pattern'>): string {
  if (view.mode !== 'album' || view.track === 1 || view.track === null) return 'Closest to a line';
  if (view.track === 2) return `Closest to ${view.pattern?.label ?? 'the pattern'}`;
  return 'Closest to PLATINUM';
}

/** Between-tracks explanation of the next track's goal. */
export function betweenGoal(next: Exclude<Track, 1>): string {
  return next === 2
    ? `First to mark every square of this pattern wins +${TRACK_POINTS[2]} pts`
    : `Every square is wild: first to fill the whole card wins +${TRACK_POINTS[3]} pts`;
}

/** Phone header: "Track 2 · The Remix" (Album) or "Song 4" (Single). */
export function phoneTrackLabel(view: Pick<DisplayView, 'mode' | 'track' | 'roundNumber'>): string {
  const track = albumTrack(view);
  return track ? `Track ${track} · ${TRACK_NAMES[track]}` : `Song ${view.roundNumber}`;
}

/** Album points badge: "⭐ 23 pts". */
export function pointsLabel(points: number): string {
  return `⭐ ${points} ${points === 1 ? 'pt' : 'pts'}`;
}

/** Mark prompt headline; `word` is the glowing part. Track 3 makes every empty square wild. */
export function markPromptCopy(track: Track | null, category: Category): { before: string; word: string; after: string } {
  return track === 3
    ? { before: 'Tap ', word: 'ANY', after: ' square — PLATINUM round!' }
    : { before: 'Tap a ', word: colorName(category), after: ' square' };
}

/** Under the mark prompt: what happens if the timer runs out (Track 3 has no "best" square). */
export function autoMarkCopy(track: Track | null): string {
  return track === 3 ? 'Auto-marks an empty square' : 'Auto-marks your best square';
}

/** Late joiner: Album deals them in at the next track; Single and Track 3 have none left. */
export function lateJoinCopy(view: Pick<DisplayView, 'mode' | 'track'>): string {
  const track = albumTrack(view);
  return track === 1 || track === 2
    ? "You'll get a card when the next track starts."
    : "You'll play from the next match.";
}

/** "3 away from a line" / "… from the 4 Corners" / "… from PLATINUM" (squaresToGoal is goal-aware). */
export function goalAway(n: number | null | undefined, view: Goal): string {
  if (n === null || n === undefined) return '';
  const track = albumTrack(view);
  if (track === 3) return n === 0 ? 'PLATINUM!' : `${n} away from PLATINUM`;
  if (track === 2) {
    const target = `the ${view.pattern?.label ?? 'pattern'}`;
    return n === 0 ? `You have ${target}!` : `${n} away from ${target}`;
  }
  return n === 0 ? 'You have a line!' : `${n} away from a line`;
}

/** What the winner finished, under their name in the celebration. */
export function winGot(view: Goal): string {
  const track = albumTrack(view);
  if (track === 2) return `completed the ${view.pattern?.label ?? 'pattern'}!`;
  if (track === 3) return 'filled the card!';
  return 'got a line!';
}

/** The big celebration wordmark: each Album track has its own word; Single is BANGER!. */
export type CelebrationWord = 'BANGER!' | 'THE REMIX!' | 'PLATINUM!';
export function celebrationWord(track: Track | null): CelebrationWord {
  return track === 3 ? 'PLATINUM!' : track === 2 ? 'THE REMIX!' : 'BANGER!';
}

/** "Josh got a BANGER!" / "Ann & Bob got The Remix!" / "Josh hit PLATINUM!"; `you` is the winner's own phone. */
export function celebrationLine(names: string[], track: Track | null, you = false): string {
  const who = you
    ? 'You'
    : names.length === 0 ? 'Someone' : names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} & ${names.at(-1)}`;
  const what = track === 3 ? 'hit PLATINUM!' : track === 2 ? 'got The Remix!' : 'got a BANGER!';
  return `${who} ${what}`;
}

/** Non-winner phones: "🎉 Josh got a BANGER!" / "🎉 Ann & Bob got The Remix!" / "🎉 Cyd hit PLATINUM!". */
export function winHeadline(names: string[], track: Track | null): string {
  return `🎉 ${celebrationLine(names, track)}`;
}
